import type { MedplumClient, WithId } from '@medplum/core';
import type {
  ActivityDefinition,
  Bundle,
  PlanDefinition,
  PlanDefinitionAction,
} from '@medplum/fhirtypes';

const PAGE_SIZE = 200;

// Canonical URLs of the ActivityDefinitions a plan's action tree references.
function referencedActivityCanonicals(pd: PlanDefinition): string[] {
  const urls = new Set<string>();
  const walk = (actions: PlanDefinitionAction[] | undefined) => {
    for (const action of actions ?? []) {
      const canonical = action.definitionCanonical?.split('|')[0];
      if (canonical) {
        urls.add(canonical);
      }
      walk(action.action);
    }
  };
  walk(pd.action);
  return [...urls];
}

// The ActivityDefinitions a plan references, fetched by canonical URL (comma =
// OR). A plan references at most a few dozen, so one page suffices. Shared by
// the encounter Care Plan views and the patient-profile detail so a stored
// plan's activities recover their exercise-intrinsic fields when rendered.
export async function fetchPlanActivityDefinitions(
  client: MedplumClient,
  pd: PlanDefinition,
): Promise<ActivityDefinition[]> {
  const urls = referencedActivityCanonicals(pd);
  if (urls.length === 0) {
    return [];
  }
  const qs = new URLSearchParams();
  qs.append('url', urls.join(','));
  qs.append('_count', String(PAGE_SIZE));
  const bundle = (await client.search(
    'ActivityDefinition',
    qs.toString(),
  )) as Bundle<ActivityDefinition>;
  return (bundle.entry ?? [])
    .map((e) => e.resource)
    .filter((r): r is WithId<ActivityDefinition> => Boolean(r?.id));
}
